/**
 * Graph renderer for position and velocity plots
 */

import type { HistoryPoint } from '../types';

interface GraphConfig {
  canvas: HTMLCanvasElement;
  title: string;
  yLabel: string;
  yMax: number;
  yMin?: number;
}

export class GraphRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private yLabel: string;
  private yMax: number;
  private yMin: number;
  private width: number;
  private height: number;

  private readonly PADDING = { top: 40, right: 20, bottom: 40, left: 60 };

  constructor(config: GraphConfig) {
    this.canvas = config.canvas;
    const ctx = config.canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D context');
    this.ctx = ctx;

    this.yLabel = config.yLabel;
    this.yMax = config.yMax;
    this.yMin = config.yMin ?? 0;

    this.width = config.canvas.width;
    this.height = config.canvas.height;

    this.setupCanvas();
  }

  private setupCanvas(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();

    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;

    this.width = this.canvas.width;
    this.height = this.canvas.height;

    this.ctx.scale(dpr, dpr);
  }


  /**
   * Convert data coordinates to canvas coordinates
   */
  private dataToCanvas(
    time: number,
    value: number,
    maxTime: number
  ): { x: number; y: number } {
    const dpr = window.devicePixelRatio || 1;
    const canvasWidth = this.width / dpr;
    const canvasHeight = this.height / dpr;

    const plotWidth = canvasWidth - this.PADDING.left - this.PADDING.right;
    const plotHeight = canvasHeight - this.PADDING.top - this.PADDING.bottom;

    const x = this.PADDING.left + (time / maxTime) * plotWidth;
    const y =
      this.PADDING.top +
      plotHeight -
      ((value - this.yMin) / (this.yMax - this.yMin)) * plotHeight;

    return { x, y };
  }

  /**
   * Draw graph axes and grid
   */
  private drawAxes(maxTime: number): void {
    const ctx = this.ctx;
    const dpr = window.devicePixelRatio || 1;
    const canvasWidth = this.width / dpr;
    const canvasHeight = this.height / dpr;

    const plotWidth = canvasWidth - this.PADDING.left - this.PADDING.right;
    const plotHeight = canvasHeight - this.PADDING.top - this.PADDING.bottom;

    // Draw grid
    ctx.strokeStyle = '#2a3248';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);

    // Vertical grid lines (time)
    const numVerticalLines = 5;
    for (let i = 0; i <= numVerticalLines; i++) {
      const x = this.PADDING.left + (i / numVerticalLines) * plotWidth;
      ctx.beginPath();
      ctx.moveTo(x, this.PADDING.top);
      ctx.lineTo(x, this.PADDING.top + plotHeight);
      ctx.stroke();
    }

    // Horizontal grid lines (value)
    const numHorizontalLines = 5;
    for (let i = 0; i <= numHorizontalLines; i++) {
      const y = this.PADDING.top + (i / numHorizontalLines) * plotHeight;
      ctx.beginPath();
      ctx.moveTo(this.PADDING.left, y);
      ctx.lineTo(this.PADDING.left + plotWidth, y);
      ctx.stroke();
    }

    ctx.setLineDash([]);

    // Draw axes
    ctx.strokeStyle = '#8b93a7';
    ctx.lineWidth = 2;

    // Y-axis
    ctx.beginPath();
    ctx.moveTo(this.PADDING.left, this.PADDING.top);
    ctx.lineTo(this.PADDING.left, this.PADDING.top + plotHeight);
    ctx.stroke();

    // X-axis
    ctx.beginPath();
    ctx.moveTo(this.PADDING.left, this.PADDING.top + plotHeight);
    ctx.lineTo(this.PADDING.left + plotWidth, this.PADDING.top + plotHeight);
    ctx.stroke();

    // Draw labels
    ctx.fillStyle = '#b8bfd3';
    ctx.font = '12px Inter, sans-serif';
    ctx.textAlign = 'center';

    // X-axis labels (time)
    for (let i = 0; i <= numVerticalLines; i++) {
      const x = this.PADDING.left + (i / numVerticalLines) * plotWidth;
      const time = (i / numVerticalLines) * maxTime;
      ctx.fillText(time.toFixed(1) + 's', x, canvasHeight - 24);
    }

    // Y-axis labels (value)
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let i = 0; i <= numHorizontalLines; i++) {
      const y = this.PADDING.top + plotHeight - (i / numHorizontalLines) * plotHeight;
      const value = this.yMin + (i / numHorizontalLines) * (this.yMax - this.yMin);
      ctx.fillText(value.toFixed(0), this.PADDING.left - 10, y);
    }

    // Y-axis label
    ctx.save();
    ctx.translate(15, canvasHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.fillStyle = '#f2f4f8';
    ctx.font = 'bold 13px Inter, sans-serif';
    ctx.fillText(this.yLabel, 0, 0);
    ctx.restore();

    // X-axis label
    ctx.textAlign = 'center';
    ctx.fillStyle = '#f2f4f8';
    ctx.font = 'bold 13px Inter, sans-serif';
    ctx.fillText('Time (s)', this.PADDING.left + plotWidth / 2, canvasHeight - 10);
  }

  /**
   * Draw a data series
   */
  private drawSeries(
    data: HistoryPoint[],
    getValue: (point: HistoryPoint) => number,
    color: string,
    maxTime: number
  ): void {
    if (data.length < 2) return;

    const ctx = this.ctx;

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();

    const firstPoint = this.dataToCanvas(data[0].time, getValue(data[0]), maxTime);
    ctx.moveTo(firstPoint.x, firstPoint.y);

    for (let i = 1; i < data.length; i++) {
      const point = this.dataToCanvas(data[i].time, getValue(data[i]), maxTime);
      ctx.lineTo(point.x, point.y);
    }

    ctx.stroke();

    // Draw dots at each point
    ctx.fillStyle = color;
    for (const dataPoint of data) {
      const point = this.dataToCanvas(dataPoint.time, getValue(dataPoint), maxTime);
      ctx.beginPath();
      ctx.arc(point.x, point.y, 2, 0, 2 * Math.PI);
      ctx.fill();
    }
  }

  /**
   * Render the graph
   */
  render(
    blueData: HistoryPoint[],
    redData: HistoryPoint[],
    getValue: (point: HistoryPoint) => number
  ): void {
    // Update canvas resolution on each render to handle resizing
    this.setupCanvas();

    const ctx = this.ctx;
    const dpr = window.devicePixelRatio || 1;
    const canvasWidth = this.width / dpr;
    const canvasHeight = this.height / dpr;

    // Clear canvas
    ctx.fillStyle = '#0f1422';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Calculate max time
    const maxTime = Math.max(
      blueData.length > 0 ? blueData[blueData.length - 1].time : 0,
      redData.length > 0 ? redData[redData.length - 1].time : 0,
      1 // minimum 1 second
    );

    // Draw axes
    this.drawAxes(maxTime);

    // Draw data series
    this.drawSeries(blueData, getValue, '#3b82f6', maxTime);
    this.drawSeries(redData, getValue, '#ef4444', maxTime);
  }
}

/**
 * Container for both graphs
 */
export class GraphsRenderer {
  private positionGraph: GraphRenderer;
  private velocityGraph: GraphRenderer;

  constructor(positionCanvas: HTMLCanvasElement, velocityCanvas: HTMLCanvasElement) {
    this.positionGraph = new GraphRenderer({
      canvas: positionCanvas,
      title: 'Position vs Time',
      yLabel: 'Altitude (m)',
      yMax: 4, // 4 meters starting altitude
      yMin: 0
    });

    this.velocityGraph = new GraphRenderer({
      canvas: velocityCanvas,
      title: 'Velocity vs Time',
      yLabel: 'Velocity (m/s)',
      yMax: 8, // 8 m/s max velocity
      yMin: 0
    });
  }

  render(
    blueHistory: HistoryPoint[],
    redHistory: HistoryPoint[],
    planeAngleDegrees: number
  ): void {
    // Convert plane angle to radians for calculation
    const planeAngleRad = planeAngleDegrees * (Math.PI / 180);
    const maxHeight = 4; // 4 meters total height

    // For position graph, convert position along plane to altitude (starting from 4m going down)
    this.positionGraph.render(
      blueHistory,
      redHistory,
      (point) => maxHeight - (point.position * Math.sin(planeAngleRad))
    );

    this.velocityGraph.render(
      blueHistory,
      redHistory,
      (point) => point.velocity
    );
  }

  resize(): void {
    // Resize will be handled automatically by setupCanvas on next render
  }
}
